import {
  EMPLOYMENT_STATUS_LABELS,
  MARITAL_STATUS_LABELS,
  RESIDENTIAL_STATUS_LABELS,
  TITLE_LABELS,
  UNEMPLOYED_BUSINESS_SECTOR_IDS,
  UNEMPLOYED_OCCUPATION_IDS,
} from '@/types/enums';
import {
  MockNegativeIdAddress,
  MockTestKbaAddress,
  MockTestOtpAddress,
  MockThinFileAddress,
} from '@/utils/dataMocks';

export const sessionTTLUpdateIntervalMs = 30000;
export const sessionTTL = 900;
export const logoutAlertModalShowTime = 120;
export const FIELD_ERROR_CLASS = 'field-error-message';

export const maritalStatuses = [
  {
    title: 'Single',
    value: 'Single',
  },
  {
    title: 'Married/Civil partnership',
    value: 'Married',
  },
  {
    title: 'Living with my partner',
    value: 'Common law',
  },
  {
    title: 'Divorced',
    value: 'Divorced',
  },
  {
    title: 'Widowed',
    value: 'Widowed',
  },
];

export const dependantsCount = [
  {
    title: 'None',
    value: '0',
  },
  {
    title: '1',
    value: '1',
  },
  {
    title: '2',
    value: '2',
  },
  {
    title: '3 or more',
    value: '3',
  },
];

export const titles = [
  {
    title: 'Mr',
    value: 'Mr',
  },
  {
    title: 'Mrs',
    value: 'Mrs',
  },
  {
    title: 'Miss',
    value: 'Miss',
  },
  {
    title: 'Ms',
    value: 'Ms',
  },
  {
    title: 'Dr',
    value: 'Dr',
  },
  {
    title: 'Rev',
    value: 'Rev',
  },
  {
    title: 'Other',
    value: 'Other',
  },
];

export const residentialStatuses = [
  {
    title: 'Homeowner',
    value: 'Homeowner',
  },
  {
    title: 'Private tenant',
    value: 'Private tenant',
  },
  {
    title: 'Council tenant',
    value: 'Council tenant',
  },
  {
    title: 'Living with parents',
    value: 'Living with parents',
  },
];

export const months = [
  { title: 'January', value: '1' },
  { title: 'February', value: '2' },
  { title: 'March', value: '3' },
  { title: 'April', value: '4' },
  { title: 'May', value: '5' },
  { title: 'June', value: '6' },
  { title: 'July', value: '7' },
  { title: 'August', value: '8' },
  { title: 'September', value: '9' },
  { title: 'October', value: '10' },
  { title: 'November', value: '11' },
  { title: 'December', value: '12' },
];

export const employmentStatuses = [
  { title: 'Full time', value: 'Full time' },
  { title: 'Part time', value: 'Part time' },
  { title: 'Self employed', value: 'Self employed' },
  { title: 'Unemployed', value: 'Unemployed' },
  { title: 'Retired', value: 'Retired' },
  { title: 'Student', value: 'Student' },
  { title: 'Homemaker', value: 'Homemaker' },
];

export const affordabilityLevels = [
  { title: 'V. Weak', score: 1, delay: 0 },
  { title: 'Weak', score: 2, delay: 1000 },
  { title: 'Fair', score: 3, delay: 2000 },
  { title: 'Good', score: 4, delay: 3000 },
  { title: 'V. Good', score: 5, delay: 4000 },
];

export const loanLengths = [
  { title: '1 year', value: '12' },
  { title: '2 years', value: '24' },
  { title: '3 years', value: '36' },
  { title: '4 years', value: '48' },
  { title: '5 years', value: '60' },
  { title: '6 years', value: '72' },
  { title: '7 years', value: '84' },
];

export const loanPurposes = [
  { title: 'Unspecified', value: 'Unspecified' },
  { title: 'Consolidate other debts', value: 'ConsolidateOtherDebts' },
  { title: 'Car purchase', value: 'CarPurchase' },
  { title: 'Holiday', value: 'Holiday' },
  { title: 'Wedding', value: 'Wedding' },
  { title: 'Home improvements', value: 'HomeImprovements' },
];

export const loanTypes = [{ title: 'Personal loan', value: 2 }];

export const WW_NEXT_MESSAGE_DELAY = 350;
export const WW_CHAT_COMPLETE_DELAY = 350;
export const WW_END_OF_FLOW_DELAY = 750;
export const WW_FIRST_MESSAGE_PUSH_DELAY = 1000;
export const WW_MESSAGE_PUSH_DELAY = 2000;

export const CREDIT_REPORT_ACCOUNT_TYPES = {
  CREDIT_CARD: 'credit-card',
  CURRENT_ACCOUNT: 'current-account',
  LOAN: 'loan',
  MORTGAGE: 'mortgage',
  OTHER: 'other',
  TELECOMMUNICATION: 'telecommunications',
  UTILITY: 'utilities',
} as const;

export const MORTGAGE_DATA_SS_KEY = 'MortgageAffordabilityData';

export const CREDIT_CARD_GOALS = [
  {
    label: '0% balance transfer',
    goalId: 'CC_BALTRANSFERV2',
    categoryCode: 'MSE_BalanceTransfer',
  },
  {
    label: '0% spending',
    goalId: 'CC_PURCHASE',
    categoryCode: 'MSE_Purchases',
  },
  {
    label: 'Credit (re)building',
    goalId: 'CC_CREDITBUILDER',
    categoryCode: 'MSE_BadCredit',
  },
  {
    label: 'Reward',
    goalId: 'CC_REWARD',
    categoryCode: 'MSE_Rewards',
  },
  {
    label: 'All-rounder',
    goalId: 'CC_BALTRANSFERPURCHASE',
    categoryCode: 'MSE_AllRounder',
  },
  {
    label: '0% money transfer',
    goalId: 'CC_MONEYTRANSFER',
    categoryCode: 'MSE_MoneyTransfers',
  },
  {
    label: 'Travel',
    goalId: 'CC_OVERSEAS',
    categoryCode: 'MSE_Travel',
  },
  {
    label: 'Airmiles',
    goalId: 'CC_REWARD_AIRLINE',
    categoryCode: 'MSE_Airline',
  },
] as const;

export const CARD_INTENT = [
  {
    label: 'Everyday spending',
    value: 'CC_EVERYDAY_SPENDING',
  },
  {
    label: 'Specific purchase',
    value: 'CC_SPECIFIC_PURCHASE',
  },
];

export const PREDEFINED_CARD_SORTS = {
  'MSE_BalanceTransfer': 'BALTRANSFER_MSE',
  'MSE_Purchases': 'PURCHASE_MSE',
  'MSE_Travel': 'OVERSEAS_MSE',
  'MSE_Rewards': 'REWARDS_MSE',
  'MSE_Airline': 'AIRMILES_MSE',
  'MSE_MoneyTransfers': 'MONEYTRANSFER_MSE',
  'MSE_BadCredit': 'CREDIT_REBUILDING_MSE',
  'MSE_AllRounder': 'ALLCARDS_APPROVAL',
};

export const LOAN_PURPOSE_OPTIONS = [
  { label: 'Car Purchase', value: 5 },
  { label: 'Consolidate Other Debts', value: 9 },
  { label: 'Home Improvements', value: 8 },
  { label: 'Wedding', value: 1 },
  { label: 'Holiday', value: 2 },
  { label: 'Other', value: 7 },
];

export const LOAN_TYPES = [
  { name: 'Personal loan', value: 2 },
  { name: 'Car Finance loan', value: 3 },
  { name: 'Guarantor loan', value: 4 },
  { name: 'Doorstep loan', value: 5 },
];

export const LOAN_TERM_IN_MONTHS = [
  { label: '1 year', value: 12 },
  { label: '2 years', value: 24 },
  { label: '3 years', value: 36 },
  { label: '4 years', value: 48 },
  { label: '5 years', value: 60 },
  { label: '6 years', value: 72 },
  { label: '7 years', value: 84 },
];

export const LOAN_SORTS = [
  {
    value: 'GR_APPROVAL',
    trackingName: 'GrEligibilityChances',
    label: 'Eligibility chances',
  },
  {
    value: 'LOWEST_APR',
    trackingName: 'APRTotalCost',
    label: 'APR/Total cost',
  },
];

export const BROKER_HEADERS = {
  'Client-ID': 17,
  'Organisation-ID': 5,
};
export const CREDIT_CARDS_HEADERS = {
  ...BROKER_HEADERS,
  'Channel-ID': 335,
};
export const LOANS_HEADERS = {
  ...BROKER_HEADERS,
  'Channel-ID': 336,
};

export const MOCK_ADDRESS_MAP = {
  kba: MockTestKbaAddress,
  otp: MockTestOtpAddress,
  negativeId: MockNegativeIdAddress,
  thinFile: MockThinFileAddress,
};

export const CSIS_HEADERS = {
  'Organisation-ID': 18,
  'Channel-ID': 334,
  'Client-ID': 17,
};

export const employmentStatusOptions = [
  {
    label: EMPLOYMENT_STATUS_LABELS.FULL_TIME,
    id: 1,
  },
  {
    label: EMPLOYMENT_STATUS_LABELS.PART_TIME,
    id: 2,
  },
  {
    label: EMPLOYMENT_STATUS_LABELS.HOMEMAKER,
    id: 5,
  },
  {
    label: EMPLOYMENT_STATUS_LABELS.STUDENT,
    id: 6,
  },
  {
    label: EMPLOYMENT_STATUS_LABELS.RETIRED,
    id: 9,
  },
  {
    label: EMPLOYMENT_STATUS_LABELS.SELF_EMPLOYED,
    id: 10,
  },
  {
    label: EMPLOYMENT_STATUS_LABELS.UNEMPLOYED,
    id: 11,
  },
];

export const titleOptions = [
  {
    label: TITLE_LABELS.MR,
    id: 1,
  },
  {
    label: TITLE_LABELS.MRS,
    id: 2,
  },
  {
    label: TITLE_LABELS.MISS,
    id: 3,
  },
  {
    label: TITLE_LABELS.MS,
    id: 4,
  },
  {
    label: TITLE_LABELS.DR,
    id: 5,
  },
  {
    label: TITLE_LABELS.REV,
    id: 6,
  },
  {
    label: TITLE_LABELS.OTHER,
    id: 7,
  },
];

export const maritalStatusOptions = [
  {
    label: MARITAL_STATUS_LABELS.DIVORCED,
    id: 1,
  },
  {
    label: MARITAL_STATUS_LABELS.MARRIED_OR_CIVIL_PARTNERSHIP,
    id: 2,
  },
  {
    label: MARITAL_STATUS_LABELS.LIVING_WITH_PARTNER,
    id: 4,
  },
  {
    label: MARITAL_STATUS_LABELS.SINGLE,
    id: 7,
  },
  {
    label: MARITAL_STATUS_LABELS.WIDOWED,
    id: 8,
  },
];

export const residentialStatusOptions = [
  RESIDENTIAL_STATUS_LABELS.HOMEOWNER,
  RESIDENTIAL_STATUS_LABELS.PRIVATE_TENANT,
  RESIDENTIAL_STATUS_LABELS.COUNCIL_TENANT,
  RESIDENTIAL_STATUS_LABELS.LIVING_WITH_PARENTS,
];

export const numberOfDependantsOptions = [
  {
    id: 0,
    label: 'None',
  },
  {
    id: 1,
    label: '1',
  },
  {
    id: 2,
    label: '2',
  },
  {
    id: 3,
    label: '3 or more',
  },
];

export const monthsInYear = [
  { label: 'January', value: '01' },
  { label: 'February', value: '02' },
  { label: 'March', value: '03' },
  { label: 'April', value: '04' },
  { label: 'May', value: '05' },
  { label: 'June', value: '06' },
  { label: 'July', value: '07' },
  { label: 'August', value: '08' },
  { label: 'September', value: '09' },
  { label: 'October', value: '10' },
  { label: 'November', value: '11' },
  { label: 'December', value: '12' },
];

export const businessSectorIdsToReset = [
  UNEMPLOYED_BUSINESS_SECTOR_IDS.HOMEMAKER,
  UNEMPLOYED_BUSINESS_SECTOR_IDS.NOT_OR_SELF_EMPLOYED,
  UNEMPLOYED_BUSINESS_SECTOR_IDS.RETIRED,
  UNEMPLOYED_BUSINESS_SECTOR_IDS.STUDENT,
];

export const occupationIdsToReset = [
  UNEMPLOYED_OCCUPATION_IDS.HOMEMAKER,
  UNEMPLOYED_OCCUPATION_IDS.NOT_OR_SELF_EMPLOYED,
  UNEMPLOYED_OCCUPATION_IDS.RETIRED,
  UNEMPLOYED_OCCUPATION_IDS.STUDENT,
];

export const DISMISSED_REPORT_INSIGHTS_KEY = 'cc-dismissedReportInsights';
export const POSITIVE_REPORT_INSIGHTS_KEY = 'cc-positiveReportInsights';
